import { ghostBtnClass, mfgPrimaryBtnClass } from "@/components/manufacturing/manufacturingSurfaceClasses";
import type {
  MfgMaterial,
  MfgMaterialUse,
  MfgRecipeDetailResponse,
  MfgRecipeKind,
  MfgRecipeSummary,
} from "@/lib/manufacturingClient";
import { useMemo, useState } from "react";
import { SectionCard } from "@/components/common/SectionCard";
import { ManufacturingMaterialUseExplainer } from "./ManufacturingMaterialUseExplainer";
import { ManufacturingModelsBomCorrectModal } from "./ManufacturingModelsBomCorrectModal";
import { useManufacturingModelsLines } from "./useManufacturingModelsLines";

import { isNegativeAdjustmentQuantity, type BomLineSign } from "@/lib/manufacturing/bomLineSign";
import { MFG_BOM_KIND_FILTERS } from "@/lib/manufacturing/recipeKinds";
import { ManufacturingModelsBomLineRow } from "./ManufacturingModelsBomLineRow";
import { ManufacturingModelsBomTree } from "./ManufacturingModelsBomTree";
import { ManufacturingNegativeAdjustmentsHeading } from "./ManufacturingNegativeAdjustmentsHeading";

const filterBtnBase =
  "rounded-md border px-2.5 py-1 text-xs font-medium transition-colors";
const filterBtnInactive =
  "border-gray-200 bg-white text-gray-700 hover:bg-gray-50 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-200 dark:hover:bg-zinc-700";
const filterBtnActive =
  "border-indigo-500 bg-indigo-600 text-white";

type Props = {
  selectedId: string | null;
  detail: MfgRecipeDetailResponse | null;
  materials: MfgMaterial[];
  recipes: MfgRecipeSummary[];
  orgSlotLabels: readonly string[];
  onError: (message: string) => void;
  onSaved: () => void;
  readOnly?: boolean;
  onOpenPartUses?: () => void;
};

export function ManufacturingModelsBomEditor({
  selectedId,
  detail,
  materials,
  recipes,
  orgSlotLabels,
  onError,
  onSaved,
  readOnly = false,
  onOpenPartUses,
}: Props) {
  const m = useManufacturingModelsLines({ selectedId, detail, materials, recipes, onError, onSaved });
  const [correctOpen, setCorrectOpen] = useState(false);
  const [treeView, setTreeView] = useState(false);

  const materialOptions = useMemo(
    () =>
      m.activeMaterials.map((mat) => ({
        key: mat.id,
        label: `${mat.code} — ${mat.name}`,
        sublabel: mat.unit,
        data: mat,
      })),
    [m.activeMaterials],
  );

  const recipeOptions = useMemo(() => {
    const opts = m.filteredRecipes.map((r) => ({
      key: r.id,
      label: `${r.code} — ${r.name}`,
      sublabel: (r.kind ?? "assembly") as MfgRecipeKind,
      data: r,
    }));
    // Keep currently selected components visible even if a kind filter or
    // truncated catalog list omitted them (e.g. option doors).
    const seen = new Set(opts.map((o) => o.key));
    for (const ln of detail?.lines ?? []) {
      const id = ln.component_recipe_id;
      if (!id || seen.has(id)) continue;
      const code = ln.component_recipe_code?.trim() || "component";
      const name = ln.component_recipe_name?.trim() || code;
      opts.push({
        key: id,
        label: `${code} — ${name}`,
        sublabel: (ln.component_recipe_kind ?? "assembly") as MfgRecipeKind,
        data: {
          id,
          code,
          name,
          notes: "",
          is_active: true,
          needs_review: false,
          kind: (ln.component_recipe_kind ?? "assembly") as MfgRecipeKind,
          tags: [],
          created_at: "",
          updated_at: "",
        },
      });
      seen.add(id);
    }
    return opts;
  }, [m.filteredRecipes, detail?.lines]);

  const useOptions = useMemo(
    () =>
      m.uses.map((u: MfgMaterialUse) => ({
        key: u.id,
        label: `${u.use_code} — ${u.name}`,
        sublabel:
          u.resolve_mode === "finish_variant"
            ? `finish · ${u.finish_channel || "?"} · ${u.unit}`
            : `use · ${u.unit}`,
        data: u,
      })),
    [m.uses],
  );

  if (!selectedId || !detail) return null;
  const recipeId = selectedId;

  const positiveRows = m.lineRows.filter((row) => !isNegativeAdjustmentQuantity(row.quantity));
  const negativeRows = m.lineRows.filter((row) => isNegativeAdjustmentQuantity(row.quantity));
  const showNegative = !readOnly || negativeRows.length > 0;

  function renderRows(rows: typeof positiveRows, sign: BomLineSign) {
    return rows.map((row) => (
      <ManufacturingModelsBomLineRow
        key={row.key}
        row={row}
        sign={sign}
        readOnly={readOnly}
        selectedId={recipeId}
        materialOptions={materialOptions}
        recipeOptions={recipeOptions}
        useOptions={useOptions}
        orgSlotLabels={orgSlotLabels}
        onError={onError}
        onSaved={onSaved}
        actions={m}
      />
    ));
  }

  function addButtons(quantity: string) {
    return (
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          className={ghostBtnClass}
          onClick={() => m.addMaterialLine(quantity)}
          disabled={!m.defaultMaterialId}
        >
          + Material
        </button>
        <button
          type="button"
          className={ghostBtnClass}
          onClick={() => m.addComponentLine(quantity)}
          disabled={!m.defaultComponentRecipeId}
        >
          + Sub-recipe
        </button>
        <button
          type="button"
          className={ghostBtnClass}
          onClick={() => m.addUseLine(quantity)}
          disabled={!m.defaultUseId}
          title={
            m.defaultUseId
              ? "Add a line that names a job (interchangeable SKUs)"
              : "Create a part use first (Part uses slide or Inventory)"
          }
        >
          + Use
        </button>
      </div>
    );
  }

  return (
    <>
    <SectionCard
      title={readOnly ? "BOM lines (read-only)" : "BOM lines"}
      description={
        <>
          Rolled-up estimate (WAC): <strong>${detail.estimated_materials_cost.toFixed(2)}</strong>
          {" · "}Mode: {detail.rollup_mode}
          {" · "}Draft (direct material lines only):{" "}
          <strong>${m.draftMaterialsEstimate.toFixed(2)}</strong>
        </>
      }
      rightSlot={
        <div className="flex flex-wrap items-center gap-2">
          {selectedId ? (
            <button
              type="button"
              className={ghostBtnClass}
              onClick={() => setTreeView((v) => !v)}
            >
              {treeView ? "Edit lines" : "Tree view"}
            </button>
          ) : null}
          {!readOnly && m.linesSaved ? (
            <span className="rounded-md border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-300">
              Model Saved
            </span>
          ) : null}
        </div>
      }
    >
      {treeView && selectedId ? (
        <div className="mb-3">
          <ManufacturingModelsBomTree recipeId={selectedId} />
        </div>
      ) : null}
      {detail.rollup_warning ? (
        <div className="mb-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-950 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100">
          <p>{detail.rollup_warning}</p>
          {onOpenPartUses ? (
            <button
              type="button"
              className={`${ghostBtnClass} mt-2`}
              onClick={onOpenPartUses}
            >
              Open Part uses
            </button>
          ) : null}
        </div>
      ) : null}
      {readOnly ? null : (
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <button
            type="button"
            className={ghostBtnClass}
            onClick={() => setCorrectOpen(true)}
          >
            Correct BOM by voice…
          </button>
          <span className="text-[11px] text-gray-500 dark:text-zinc-400">
            Slot-aware math (perimeter formulas, round-up) and add materials.
          </span>
        </div>
      )}

      {readOnly ? null : (
        <ManufacturingMaterialUseExplainer
          variant="bom"
          action={
            m.uses.length === 0 && onOpenPartUses ? (
              <button type="button" className={ghostBtnClass} onClick={onOpenPartUses}>
                Open Part uses…
              </button>
            ) : m.uses.length === 0 ? (
              <p className="text-xs text-sky-900/80 dark:text-sky-100/75">
                No uses yet — create one under Part uses, or from Inventory (select 2+ materials).
              </p>
            ) : null
          }
        />
      )}

      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-xs text-gray-500 dark:text-zinc-400">Filter sub-recipes by kind:</span>
        {MFG_BOM_KIND_FILTERS.map((opt) => (
          <button
            key={opt.id}
            type="button"
            className={`${filterBtnBase} ${m.kindFilter === opt.id ? filterBtnActive : filterBtnInactive}`}
            onClick={() => m.setKindFilter(opt.id)}
          >
            {opt.label}
          </button>
        ))}
      </div>

      <div className="space-y-2">
        {positiveRows.length === 0 ? (
          <div className="text-xs text-gray-500 dark:text-zinc-400">No lines yet.</div>
        ) : (
          renderRows(positiveRows, "positive")
        )}
      </div>

      {readOnly ? null : addButtons("1")}

      {showNegative ? (
        <>
          <ManufacturingNegativeAdjustmentsHeading />
          <div className="space-y-2">
            {negativeRows.length === 0 ? (
              <div className="text-xs text-gray-500 dark:text-zinc-400">No savings lines.</div>
            ) : (
              renderRows(negativeRows, "negative")
            )}
          </div>
          {readOnly ? null : addButtons("-1")}
        </>
      ) : null}

      {readOnly ? null : (
        <div>
          <button
            type="button"
            className={mfgPrimaryBtnClass}
            onClick={() => void m.onSaveLines()}
            disabled={m.saveBusy}
          >
            {m.saveBusy ? "Saving…" : "Save BOM"}
          </button>
        </div>
      )}

    </SectionCard>
    {correctOpen && !readOnly ? (
      <ManufacturingModelsBomCorrectModal
        detail={detail}
        materials={materials}
        orgSlotLabels={orgSlotLabels}
        onClose={() => setCorrectOpen(false)}
        onSaved={onSaved}
      />
    ) : null}
    </>
  );
}
